import { useQuery } from '@tanstack/react-query';
import type { AppAccount } from './useAppAuth';
import { useAppAuth } from './useAppAuth';
import { createApiClient, profileService } from '../services/api';

function roleFromClaims(account: AppAccount | undefined): string {
  const claims = account?.idTokenClaims as Record<string, unknown> | undefined;
  return ((claims?.extension_Role as string) ?? '').toLowerCase();
}

/**
 * Returns the current user's role, falling back to the profile API when the
 * MSAL token has no extension_Role claim (dev mode or missing CIAM mapping).
 *
 * Uses the same ['profile'] queryKey as NavBar so the fetch is shared from cache.
 */
export function useRole() {
  const { account, getToken } = useAppAuth();
  const claimRole = roleFromClaims(account);

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: () => profileService(createApiClient(getToken)).getProfile(),
    enabled: !!account && !claimRole,
    staleTime: 5 * 60 * 1000,
  });

  const role = claimRole || (profile?.role?.toLowerCase() ?? '');

  return {
    role,
    isAdminOrOps: role === 'admin' || role === 'ops',
    isCarrierOrWorkshop: role === 'carrier' || role === 'workshop',
    isRoleLoading: !!account && !claimRole && profileLoading,
  };
}
